import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress, 
    FormControlLabel,
    IconButton,
    Modal,
    Typography,
} from "@mui/material";
import SearchField from "../SearchField/searchField"; 
import UserProfileIcon from "../../../assets/icons/userProfileIcon"; 
import CrossIcon from "../../../assets/icons/crossIcon"; 
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@store/store.tsx";
import { addUsersToTicket, getUsersOfOrg, resetOrgUsers } from "@store/userSlice.tsx"; 

interface OrgUser {
    userId: number;
    hospitalId: string;
    zendeskSupportGroupId: number;
    zendeskSupportGroup: null;
    zendeskUserId: number;
    zendeskOrgId: null;
    securityGroupId: number;
    securityGroupName: null;
    firstName: string;
    lastName: string;
    emailID: string;
    countryCode: null;
    contactNumber: null;
    isOnCallStatus: boolean;
    userTypeId: number;
    userType: null; 
    isActive: boolean;
    deactivationReson: null;
    createdBy: number;
    createdDate: string;
    updatedBy: null;
    updatedDate: null;
    totalCount: null;
}
interface AddUserModalProps {
    addUserModal: boolean;
    setAddUserModal: React.Dispatch<React.SetStateAction<boolean>>;
    ticketId?: number;
}

const AddUserModal: React.FC<AddUserModalProps> = ({
    addUserModal,
    setAddUserModal,
    ticketId,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const [originalSelectedUserIds, setOriginalSelectedUserIds] = useState<number[]>([]);
    const [currentSelectedUserIds, setCurrentSelectedUserIds] = useState<number[]>([]);
    const [searchUsers, setSearchUsers] = useState("");
    const user = useSelector((state: RootState) => state.user.user);
    const {ticketDetails} = useSelector((state: RootState) => state.tickets);
    const hospitalId = user.hospitalId;
    const { orgUsers, orgUsersLoading, isLoading: isAddingUser } = useSelector((state: RootState) => state.user);
    let orgUsersData = orgUsers.filter((orgUser)=> orgUser.userId !== user.userId);

    useEffect(() => {
        if (addUserModal && hospitalId) {
            dispatch(getUsersOfOrg({ ticketId, organizationId: String(hospitalId) }));
        }

        if (!addUserModal) {
            dispatch(resetOrgUsers());
            setOriginalSelectedUserIds([]);
            setSearchUsers("");
       }
    }, [addUserModal, hospitalId]);

    useEffect(()=>{
        if (ticketDetails?.request?.email_Cc_Ids?.length>0) {
            const usersData = orgUsersData.filter((user:any)=> ticketDetails?.request?.email_Cc_Ids.includes(user?.zendeskUserId));
            const userIds = usersData.map((item)=> item.userId);
            setOriginalSelectedUserIds(userIds);
            setCurrentSelectedUserIds(userIds);
        }
    }, [ticketDetails])

    const handleSearchTicket = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchUsers(event.target.value);
    };

    const filteredUsers = useMemo(() => {
        if (!Array.isArray(orgUsersData)) {
            return [];
        }
        return orgUsersData.filter((user: OrgUser) =>
            `${user.firstName} ${user.lastName}`
                .toLowerCase()
                .includes(searchUsers.toLowerCase())
        );
    }, [searchUsers, orgUsers]);

    const handleFilterChange = (userId: number) => {
        setCurrentSelectedUserIds((prevSelected) =>
            prevSelected.includes(userId)
              ? prevSelected.filter((id) => id !== userId)
              : [...prevSelected, userId]
          );
    };

    const [justSubmitted, setJustSubmitted] = useState(false);
    useEffect(() => {
        if (justSubmitted && !isAddingUser) {
             setAddUserModal(false);
             setJustSubmitted(false);
        }
    }, [isAddingUser, justSubmitted, setAddUserModal]);

    const handleAddUsers = () => {
        const updates: { action: 'put' | 'delete'; userId: number }[] = [];
        currentSelectedUserIds.forEach((id) => {
          if (!originalSelectedUserIds.includes(id)) {
            updates.push({ userId: id, action: 'put' });
          }
        });
        originalSelectedUserIds.forEach((id) => {
          if (!currentSelectedUserIds.includes(id)) {
            updates.push({ userId: id, action: 'delete' });
          }
        });

        if (ticketId && updates.length > 0) {
            dispatch(addUsersToTicket({ ticketId, userIds:updates }));
             setJustSubmitted(true);
        } else {
            console.warn("Add users cancelled: No ticket ID or no users selected.");
        }
    };

    const handleClose = () => {
         setAddUserModal(false);
    }

    return (
        <Modal
            open={addUserModal}
            onClose={handleClose}
            aria-labelledby="modal-modal-title" 
            aria-describedby="modal-modal-description" 
        >
            <Box
                sx={{
                    position: "relative", 
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "80%", sm: "45%" },
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    borderRadius: "4px",
                    pt: 2, 
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center", 
                }}
            >
                <Box className="px-4 pt-4 md:px-8"> 
                    <SearchField
                        style={{ padding: "6px 0 ", fontSize: "16px" }} 
                        sx={{ marginLeft: 1, flex: 1, padding: 0 }} 
                        placeHolder="Search User"
                        handleSearchTicket={handleSearchTicket}
                        value={searchUsers} 
                    />
                    <Typography variant="subtitle2" className="pt-3 text-[#A1A3A6]">
                        Recents
                    </Typography>
                </Box>
                <Box
                    sx={{
                        maxHeight: "12rem", 
                        overflowY: "auto",
                        "::-webkit-scrollbar": {
                            width: "5px",
                        },

                        "::-webkit-scrollbar-track": {
                            backgroundColor: "#f1f1f1",
                        },

                        "::-webkit-scrollbar-thumb": {
                            backgroundColor: "#C0C0C0",
                        },
                        "::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "gray",
                        },
                    }}
                    className="grid grid-cols-1 divide-y " 
                >
                    {orgUsersLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {!orgUsersLoading && filteredUsers.length === 0 && (
                         <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
                            {orgUsersData.length > 0 ? 'No matching users found.' : 'No users found.'}
                        </Typography>
                    )}

                    {!orgUsersLoading && filteredUsers.length > 0 &&
                        filteredUsers.map((user) => ( 
                            <FormControlLabel
                                key={user.userId} 
                                className="pl-2 md:pl-6 py-2 !mx-0 hover:bg-zinc-100" 
                                control={
                                    <Checkbox
                                        checked={currentSelectedUserIds.includes(user.userId)}
                                        onChange={() => handleFilterChange(user.userId)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Box className="flex"> 
                                        <IconButton> 
                                            <UserProfileIcon/>
                                        </IconButton>
                                        <Box>
                                            <Typography className="!font-semibold"> 
                                                {user.firstName} {user.lastName}{" "}
                                            </Typography>
                                            <Typography variant="subtitle2">
                                                 {user.userType} 
                                            </Typography>
                                        </Box>
                                    </Box>
                                }
                            />
                        ))}
                </Box>
                <Button
                    style={{
                        backgroundColor: "#0077B2",
                        color: "#fff",
                        textTransform: "none",
                        borderRadius: "initial", 
                        fontSize: "1rem",
                    }}
                    onClick={handleAddUsers}
                    disabled={currentSelectedUserIds?.length === 0 || isAddingUser || orgUsersLoading} // Disable logic
                >
                     {isAddingUser && justSubmitted ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                    Apply
                </Button>

                <Typography
                    component={IconButton}
                    onClick={handleClose}
                    sx={{ 
                        position: 'absolute',
                        top: 8, 
                        right: 8,
                        p: 0.5 
                    }}
                    aria-label="close add user modal" 
                >
                    <CrossIcon />
                </Typography>
            </Box>
        </Modal>
    );
   
};

export default AddUserModal;